import { createAsyncThunk } from "@src/store/createAsyncThunk";
import { getAiChatSessionId } from "@src/store/slices/aiChat";
import { experienceBuilderAiSlice } from "@src/store/slices/experienceBuilder/ai/slice";
import { fireVivTurn } from "@src/store/slices/experienceBuilder/ai/thunks/fireVivTurn";
import { getPageState } from "@src/store/slices/experienceBuilder/common/selectors/getPageState";
import { selectedExperience } from "@src/store/slices/experiences";
import { mpVivLibraryIdeaChosen } from "@src/utils/mixpanel";

import { ideasSlice } from "../slice";
import type { Idea, IdeaSource } from "../types";

const { closeLibrary } = ideasSlice.actions;

export interface SendIdeaPayload {
  idea: Idea;
  /** Where the merchant picked it — drives the `is_default` tracking property. */
  source: IdeaSource;
}

/**
 * Send an Idea Library prompt through Viv: fire a `user-first` turn with the
 * idea's prompt visible and its guide attached as a hidden `<instruction>` — the
 * model reads the guide and runs the follow-ups; the merchant only sees the
 * prompt — then dismiss the library. Shared by the empty-state chips and the
 * library list.
 *
 * Dispatches `fireVivTurn` — the same path the composer's submit takes (session
 * init + the optimistic user bubble) — so it works from anywhere, including the
 * library slide-over outside the chat panel. No-ops when the preview isn't ready
 * (firing then would capture a blank/loading page), leaving the library open.
 */
export const sendIdea = createAsyncThunk<void, SendIdeaPayload>(
  "experienceBuilderIdeas/sendIdea",
  ({ idea, source }, { dispatch, getState }) => {
    // The same gates the composer applies: never fire against a preview that
    // isn't ready, and never stack a turn on one already running (reachable now
    // that the toolbar can open the library mid-conversation).
    if (getPageState(getState()).type !== "ready") return;
    if (experienceBuilderAiSlice.selectors.getIsAiBusy(getState())) return;
    // Semi-synthetic user message: the visible prompt leads (the backend counts
    // it as a real user turn — only a message that *starts* with a system tag is
    // synthetic), with the guide riding in a trailing `<instruction>` block the
    // model reads verbatim but the merchant never sees.
    const guide = idea.guide.trim();
    const content = guide
      ? `${idea.prompt}\n\n<instruction>${guide}</instruction>`
      : idea.prompt;
    // Ideas are only reachable before the first turn, but `openBuilder` seeded
    // the session id, so this is the id `prepareChatMutation` then uses for the
    // turn below (`aiChat.sessionId || v4()`) — the event joins to that chat.
    mpVivLibraryIdeaChosen({
      experienceId: selectedExperience(getState())?.id,
      chatId: getAiChatSessionId(getState()),
      ideaId: idea.id,
      idea: idea.prompt,
      isDefault: source === "featured",
    });
    void dispatch(
      fireVivTurn({ kind: "user-first", input: { type: "text", content } })
    );
    // Selecting an idea dismisses the library — a no-op when it's already closed
    // (e.g. the empty-state chips).
    dispatch(closeLibrary());
  }
);
